import { useState, useEffect } from 'react'
import { Photo, RouteData } from '@cya-routes/shared'
import { ChevronLeft, ChevronRight, X, Camera, MapPin, Calendar, Route, Maximize2 } from 'lucide-react'

interface CompactPhotoModalProps {
  photo: Photo | null
  photos: Photo[]
  isOpen: boolean
  onClose: () => void
  onNavigate: (direction: 'prev' | 'next') => void
  routes: RouteData[]
}

export function CompactPhotoModal({ photo, photos, isOpen, onClose, onNavigate, routes }: CompactPhotoModalProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [viewportScale, setViewportScale] = useState(1)
  
  // Update viewport scale based on window size
  useEffect(() => {
    const updateScale = () => {
      // Responsive scaling based on screen width
      let scale = 1 // Default scale for desktop
      
      if (window.innerWidth < 640) { // Mobile phones
        scale = 0.65
      } else if (window.innerWidth < 768) { // Small tablets
        scale = 0.75
      } else if (window.innerWidth < 1024) { // Tablets
        scale = 0.85
      }
      
      setViewportScale(scale)
    }

    updateScale()
    window.addEventListener('resize', updateScale)
    return () => window.removeEventListener('resize', updateScale)
  }, [])
  
  if (!isOpen || !photo) return null
  
  // Debug log
  console.log('Photo modal data:', {
    photoId: photo.id,
    photoRouteId: photo.routeId,
    distanceAlongRoute: photo.distanceAlongRoute,
    takenAt: photo.takenAt,
    takenAtType: typeof photo.takenAt,
    hasToDate: photo.takenAt && typeof photo.takenAt === 'object' && 'toDate' in photo.takenAt,
    hasSeconds: photo.takenAt && typeof photo.takenAt === 'object' && 'seconds' in photo.takenAt,
    routesAvailable: routes.map(r => ({ id: r.id, name: r.name }))
  })

  const currentIndex = photos.findIndex(p => p.id === photo.id)
  const hasMultiplePhotos = photos.length > 1
  
  // Find which route this photo belongs to
  const photoRoute = routes.find(r => r.id === photo.routeId)
  
  console.log('Photo route match:', {
    found: !!photoRoute,
    photoRouteId: photo.routeId,
    photoRouteName: photoRoute?.name,
    photoRouteStats: photoRoute?.stats
  })
  
  // Get location string - now just returns coordinates if no address
  const getLocationString = () => {
    if (photo.coordinates) {
      return `${photo.coordinates.lat.toFixed(6)}, ${photo.coordinates.lng.toFixed(6)}`
    }
    return 'Unknown location'
  }

  return (
    <>
      {/* Backdrop - transparent, doesn't block map interaction */}
      <div 
        className="fixed inset-0 bg-black/10 z-40 pointer-events-none" 
      />
      
      {/* Compact Modal - 20% bigger total with larger photo */}
      <div 
        className="fixed right-4 top-20 w-80 h-[600px] bg-gray-800 rounded-xl shadow-2xl border border-gray-700 z-50 flex flex-col"
        style={{ 
          transform: `scale(${viewportScale})`,
          transformOrigin: 'top right'
        }}
      >
        {/* Header - more compact with single line */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <Camera className="w-4 h-4 text-emerald-400" />
            <h3 className="text-base font-semibold text-white">Route Photo</h3>
            {hasMultiplePhotos && (
              <>
                <span className="text-gray-500">•</span>
                <span className="text-sm text-gray-400">
                  {currentIndex + 1} of {photos.length}
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsExpanded(true)}
              className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors"
              title="Expand photo"
            >
              <Maximize2 className="w-4 h-4 text-gray-400" />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Photo - proportionally increased */}
          <div className="relative h-80 bg-gray-900 flex-shrink-0">
            <img
              src={photo.url}
              alt={photo.fileName || 'Route photo'}
              className="w-full h-full object-cover"
            />
            
            {/* Navigation overlay */}
            {hasMultiplePhotos && (
              <>
                <button
                  onClick={() => onNavigate('prev')}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
                >
                  <ChevronLeft className="w-6 h-6 text-white" />
                </button>
                <button
                  onClick={() => onNavigate('next')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
                >
                  <ChevronRight className="w-6 h-6 text-white" />
                </button>
              </>
            )}
          </div>

          <div className="p-3 space-y-3 overflow-y-auto flex-1">
            {/* Caption */}
            {photo.caption && (
              <p className="text-gray-200 text-sm italic">{photo.caption}</p>
            )}

            {/* Info items */}
            <div className="space-y-2">
              {/* Date taken */}
              {photo.takenAt && (
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <p className="text-gray-300">
                    {(() => {
                      try {
                        let dateToFormat: Date | null = null
                        
                        // Check if it's a Firestore Timestamp with toDate method
                        if (photo.takenAt && typeof photo.takenAt === 'object' && 'toDate' in photo.takenAt) {
                          dateToFormat = (photo.takenAt as any).toDate()
                        }
                        // Check if it's a Firebase Timestamp with seconds property
                        else if (photo.takenAt && typeof photo.takenAt === 'object' && 'seconds' in photo.takenAt) {
                          dateToFormat = new Date((photo.takenAt as any).seconds * 1000)
                        }
                        // Check if it's already a Date object
                        else if (photo.takenAt instanceof Date) {
                          dateToFormat = photo.takenAt
                        }
                        // Try to parse as string or number
                        else if (photo.takenAt) {
                          dateToFormat = new Date(photo.takenAt as any)
                        }
                        
                        if (!dateToFormat || isNaN(dateToFormat.getTime())) {
                          return 'Date not available'
                        }
                        
                        // Format the date
                        return new Intl.DateTimeFormat('en-AU', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        }).format(dateToFormat)
                      } catch (error) {
                        console.error('Date parsing error:', error)
                        return 'Date not available'
                      }
                    })()}
                  </p>
                </div>
              )}

              {/* Route segment with position */}
              {photoRoute && (
                <div className="space-y-1">
                  <div className="flex items-start gap-2 text-sm">
                    <Route className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-gray-300 font-medium">
                          {photoRoute.name}
                        </p>
                        {routes.length > 1 && (
                          <span className="text-xs text-gray-400">
                            (Segment {routes.findIndex(r => r.id === photoRoute.id) + 1} of {routes.length})
                          </span>
                        )}
                      </div>
                      {photo.distanceAlongRoute !== undefined && photoRoute.stats?.distance && (
                        <p className="text-xs text-gray-400 mt-1">
                          {photo.distanceAlongRoute.toFixed(1)}km of {photoRoute.stats.distance.toFixed(1)}km
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Detailed Location */}
              <div className="space-y-1">
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div>
                    {photo.location ? (
                      <>
                        {/* Show structured location data */}
                        {photo.location.street && (
                          <p className="text-gray-300">{photo.location.street}</p>
                        )}
                        <p className="text-xs text-gray-400">
                          {[
                            photo.location.city,
                            photo.location.region || photo.location.state,
                            photo.location.country
                          ].filter(Boolean).join(', ')}
                        </p>
                      </>
                    ) : photo.address ? (
                      <>
                        {/* Fallback to address string */}
                        <p className="text-gray-300">{photo.address.split(',')[0]}</p>
                        <p className="text-xs text-gray-400">{photo.address.split(',').slice(1).join(',').trim()}</p>
                      </>
                    ) : (
                      <p className="text-gray-300">{getLocationString()}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Expanded Photo View */}
      {isExpanded && (
        <div className="fixed inset-0 bg-black/95 z-[60] flex items-center justify-center">
          {/* Close button */}
          <button
            onClick={() => setIsExpanded(false)}
            className="absolute top-4 right-4 p-2 bg-gray-800/80 hover:bg-gray-700/80 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          
          {/* Navigation buttons */}
          {hasMultiplePhotos && (
            <>
              <button
                onClick={() => onNavigate('prev')}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
              >
                <ChevronLeft className="w-8 h-8 text-white" />
              </button>
              <button
                onClick={() => onNavigate('next')}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
              >
                <ChevronRight className="w-8 h-8 text-white" />
              </button>
            </>
          )}
          
          {/* Photo and info */}
          <div className="max-w-[90vw] max-h-[90vh] flex flex-col">
            {/* Photo */}
            <img
              src={photo.url}
              alt={photo.fileName || 'Route photo'}
              className="max-w-full max-h-[80vh] object-contain"
              onClick={() => setIsExpanded(false)}
            />
            
            {/* Caption at bottom */}
            {(photo.caption || (photoRoute && photo.distanceAlongRoute !== undefined)) && (
              <div className="mt-4 text-center">
                {photo.caption && (
                  <p className="text-white text-lg mb-2">{photo.caption}</p>
                )}
                {photoRoute && photo.distanceAlongRoute !== undefined && photoRoute.stats?.distance && (
                  <p className="text-gray-400 text-sm">
                    {photoRoute.name} • {photo.distanceAlongRoute.toFixed(1)}km of {photoRoute.stats.distance.toFixed(1)}km
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}